
class BigDataCorpSyncService {
  constructor() {
    this.listeners = new Map();
    this.syncIntervals = new Map();
    this.lastSyncHashes = new Map();
    this.syncIntervalMs = 15000; // 15 segundos
    this.isActive = false;
  }

  /**
   * @param {string} condoId 
   * @param {Function} callback
   */
  startSyncForCondo(condoId, callback) {
    console.log(`[BigDataCorp Sync] Iniciando sync para condo: ${condoId}`);
    
    this.isActive = true;

    if (!this.listeners.has(condoId)) {
      this.listeners.set(condoId, []);
    }
    this.listeners.get(condoId).push(callback);

    this.loadCachedState(condoId);

    this.performSync(condoId);

    this.schedulePeriodicalSync(condoId);
  }

  /**
   * @param {string} condoId 
   */
  stopSyncForCondo(condoId) {

    if (this.syncIntervals.has(condoId)) {
      clearInterval(this.syncIntervals.get(condoId));
      this.syncIntervals.delete(condoId);
    }

    this.listeners.delete(condoId);
    
    this.lastSyncHashes.delete(condoId);
  }

  stopAll() {
    
    this.syncIntervals.forEach((interval) => {
      clearInterval(interval);
    });

    this.syncIntervals.clear();
    this.listeners.clear();
    this.lastSyncHashes.clear();
    this.isActive = false;
  }

  /**
   * @param {string} condoId 
   */
  async forceSync(condoId) {
    await this.performSync(condoId);
  }

  /**
   * @param {string} condoId 
   */
  async syncAfterSave(condoId) {
    setTimeout(async () => {
      if (this.isActive && this.syncIntervals.has(condoId)) {
        await this.performSync(condoId);
      }
    }, 5000);
  }

  /**
   * @param {string} condoId 
   * @private
   */
  async performSync(condoId) {
    try {
      const { default: UnitsManagementService } = await import('./units-management.service');
      const data = await UnitsManagementService.getUnits(condoId);
      
      if (!data || !data.units) {
        console.warn(`[BigDataCorp Sync] Nenhum dado retornado para condo: ${condoId}`);
        return;
      }

      const currentHash = this.generateUnitsHash(data.units);
      const previousHash = this.lastSyncHashes.get(condoId);

      if (previousHash && previousHash !== currentHash) {
        this.notifyListeners(condoId, data);
      }

      this.lastSyncHashes.set(condoId, currentHash);
      this.updateCache(condoId, currentHash);

    } catch (error) {
      console.error(`[BigDataCorp Sync] Erro na sincronização para condo ${condoId}:`, error);
    }
  }

  /**
   * @param {string} condoId 
   * @private
   */
  schedulePeriodicalSync(condoId) {
    if (this.syncIntervals.has(condoId)) {
      clearInterval(this.syncIntervals.get(condoId));
    }

    const interval = setInterval(async () => {
      if (this.isActive) {
        await this.performSync(condoId);
      }
    }, this.syncIntervalMs);

    this.syncIntervals.set(condoId, interval);
  }

  /**
   * @param {Array} units
   * @returns {string} Hash JSON string
   * @private
   */
  generateUnitsHash(units) {
    const hashData = units.map(unit => ({
      unitId: unit.unitId || unit._id,
      enabled: unit.bigDataCorp?.enabled,
      email: unit.bigDataCorp?.email,
      alertSettings: {
        enabled: unit.bigDataCorp?.alertSettings?.enabled,
        unitEmail: unit.bigDataCorp?.alertSettings?.unitEmail,
        whatsappEnabled: unit.bigDataCorp?.alertSettings?.whatsappEnabled,
        alertTypes: unit.bigDataCorp?.alertSettings?.alertTypes
      }
    }));

    return JSON.stringify(hashData);
  }

  /**
   * @param {string} condoId 
   * @private
   */
  loadCachedState(condoId) {
    try {
      const cacheKey = `bigdatacorp_sync_${condoId}`;
      const cachedHash = localStorage.getItem(cacheKey);
      
      if (cachedHash) {
        this.lastSyncHashes.set(condoId, cachedHash);
      }
    } catch (error) {
      console.error('[BigDataCorp Sync] Erro ao carregar cache:', error);
    }
  }

  /**
   * @param {string} condoId 
   * @param {string} hash - Hash a ser salvo
   * @private
   */
  updateCache(condoId, hash) {
    try {
      const cacheKey = `bigdatacorp_sync_${condoId}`;
      localStorage.setItem(cacheKey, hash);
    } catch (error) {
      console.error('[BigDataCorp Sync] Erro ao atualizar cache:', error);
    }
  }

  /**
   * @param {string} condoId 
   * @param {Object} data
   * @private
   */
  notifyListeners(condoId, data) {
    const callbacks = this.listeners.get(condoId) || [];
    
    const notificationData = {
      ...data,
      hasChanges: true,
      timestamp: new Date().toISOString()
    };
    
    callbacks.forEach((callback) => {
      try {
        callback(notificationData);
      } catch (error) {
        console.error('[BigDataCorp Sync] Erro ao notificar listener:', error);
      }
    });
  }

  /**
   * @param {string} condoId
   */
  clearCache(condoId) {
    try {
      const cacheKey = `bigdatacorp_sync_${condoId}`;
      localStorage.removeItem(cacheKey);
      this.lastSyncHashes.delete(condoId);
    } catch (error) {
      console.error('[BigDataCorp Sync] Erro ao limpar cache:', error);
    }
  }

  /**
   * @param {string} condoId
   * @returns {Object} Status
   */
  getSyncStatus(condoId) {
    return {
      isActive: this.isActive,
      hasInterval: this.syncIntervals.has(condoId),
      hasListeners: this.listeners.has(condoId) && this.listeners.get(condoId).length > 0,
      lastHash: this.lastSyncHashes.get(condoId) || null,
      intervalMs: this.syncIntervalMs
    };
  }
  /**
   * @param {string} event 
   * @param {Function} callback 
   */
  addListener(event, callback) {
    if (event === 'syncUpdate' || event === 'syncStatus') {
      this.currentCallback = callback;
    }
  }

  /**
   * @param {string} event 
   * @param {Function} callback 
   */
  removeListener(event, callback) {
    if (event === 'syncUpdate' || event === 'syncStatus') {
      this.currentCallback = null;
    }
  }

  /**
   * @param {string} condoId 
   */
  startSync(condoId) {
    this.currentCondoId = condoId;
    this.startSyncForCondo(condoId, (data) => {
      if (this.currentCallback) {
        this.currentCallback(data);
      }
    });
    if (this.currentCallback) {
      this.currentCallback({ type: 'syncStatus', isActive: true });
    }
  }

  stopSync() {
    if (this.currentCondoId) {
      this.stopSyncForCondo(this.currentCondoId);
      this.currentCondoId = null;
    }
    
    // Notify sync status
    if (this.currentCallback) {
      this.currentCallback({ type: 'syncStatus', isActive: false });
    }
  }

  /**
   * @returns {BigDataCorpSyncService}
   */
  static getInstance() {
    if (!BigDataCorpSyncService.instance) {
      BigDataCorpSyncService.instance = new BigDataCorpSyncService();
    }
    return BigDataCorpSyncService.instance;
  }
}

BigDataCorpSyncService.instance = null;

export default BigDataCorpSyncService;